import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../globalInspector/axiosInstance";
import { env } from "../../../env";
import CommonToast from "../../../components/_utils/CommonToast";
import ICONS from "../../../assets/Icons";

const baseUrl = env.REACT_APP_BASE_URL;

export const beneInternalDashboard = createAsyncThunk(
  "beneInternalDashboard",
  async ({ page, page_size }) => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/accounts/internal`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          page_no: page,
          page_size: page_size,
        },
      });
      return response.data;
    } catch (error) {
      CommonToast({
        text: error.response.data.error,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      throw error;
    }
  },
);
export const beneInternalViewAllAcc = createAsyncThunk(
  "beneInternalViewAllAcc",
  async (values) => {
    const {
      page_no,
      page_size,
      approval_status,
      categoryName,
      search_string,
      bank_account_no,
      ifsc,
      start_date,
      end_date,
      pan,
      labels,
    } = values;
    try {
      const response = await axiosInstance.get(`${baseUrl}/accounts/internal`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          page_no: page_no,
          page_size: page_size,
          approval_status:
            approval_status && approval_status?.length !== 0
              ? [approval_status]
              : "",
          category: categoryName,
          search_string: search_string,
          start_date,
          end_date,
          bank_account_no: bank_account_no,
          ifsc: ifsc,
          pan: pan,
          labels: labels,
        },
      });
      return response.data;
    } catch (error) {
      CommonToast({
        text: error.response.data.error,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      throw error;
    }
  },
);

export const viewInternalAccountDetail = createAsyncThunk(
  "viewInternalAccountDetail",
  async (values) => {
    const { id } = values ? values : { id: null };

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/internal/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);
export const beneInternalRequiredApproval = createAsyncThunk(
  "beneInternalRequiredApproval",
  async (values) => {
    const {
      page_no,
      page_size,
      categoryName,
      search_string,
      bank_account_no,
      ifsc,
      start_date,
      end_date,
      pan,
      labels,
    } = values.queryParams || values;

    try {
      const response = await axiosInstance.get(`${baseUrl}/accounts/internal`, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          approval_status: ["pending"],
          page_no: page_no,
          page_size: page_size,
          category: categoryName,
          search_string: search_string,
          start_date: start_date,
          end_date: end_date,
          bank_account_no: bank_account_no,
          ifsc: ifsc,
          pan: pan,
          labels: labels,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const beneInternalWaitingApproved = createAsyncThunk(
  "beneWaitingApproved",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/${id}/approve`,
        {},
        {
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return rejectWithValue(undefined);
      }

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
export const beneInternalDeniedApproval = createAsyncThunk(
  "beneInternalDeniedApproval",
  async ({ id, AccountIdData }, { rejectWithValue }) => {
    try {
      const data = {
        txn_id: [id],
        rejection_reason: AccountIdData,
      };

      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/${id}/reject`,
        data,
        {
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return rejectWithValue(undefined);
      }

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
export const editInternal = createAsyncThunk(
  "editInternal",
  async (values, { rejectWithValue }) => {
    const { id, recipient_email, navigate, email, name, contact_no } = values;

    const requestOptions = {
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": sessionStorage.getItem("q2p_token"),
      },
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/${id}/edit_other_details`,
        {
          internal_accounts: {
            recipient_email,
            email,
            name,
            contact_no,
          },
        },
        requestOptions,
      );

      return response.data;
    } catch (error) {
      let errorMessage;

      if (error.response) {
        if (error.response.data?.errors?.user_unauthorized) {
          navigate("/feature_not_assigned", {
            state: { unavailableService: "IUAccount" },
          });
        }
        errorMessage = error.response.data?.errors?.recipient_email?.[0];
      } else {
        errorMessage = error.message;
      }
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);

const internalSlice = createSlice({
  name: "internalAcc",
  initialState: {
    internalAccountAvailableLabels: [],
    beneInternalDashboardAccData: null,
    beneInternalDashboardAccLoading: null,
    beneInternalDashboardAccError: null,
    beneInternalViewAllAccData: null,
    beneInternalViewAllAccLoading: null,
    beneInternalViewAllAccError: null,
    viewInternalAccountDetailData: null,
    viewInternalAccountDetailLoading: null,
    viewInternalAccountDetailError: null,
    beneInternalRequiredApprovalData: null,
    beneInternalRequiredApprovalLoading: null,
    beneInternalRequiredApprovalError: null,
    beneInternalWaitingApprovalData: null,
    beneInternalWaitingApprovalLoading: null,
    beneInternalWaitingApprovalError: null,
    beneInternalDeniedApprovalData: null,
    beneInternalDeniedApprovalLoading: null,
    beneInternalDeniedApprovalError: null,
    toggle: false,
    editInternalData: null,
    editInternalLoading: null,
    editInternalError: null,
  },
  reducers: {
    resetViewInternalAccountDetailData(state) {
      state.viewInternalAccountDetailData = null;
      state.viewInternalAccountDetailError = null;
    },
    resetInternalToggle(state) {
      state.toggle = false;
    },
    setAvailabelLabels: (state, action) => {
      state.internalAccountAvailableLabels = action.payload;
    },
    resetEditInternalData: (state, action) => {
      state.editInternalData = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(editInternal.pending, (state) => {
      state.editInternalLoading = true;
    });
    builder.addCase(editInternal.fulfilled, (state, action) => {
      state.editInternalLoading = false;
      state.editInternalData = action.payload;
    });
    builder.addCase(editInternal.rejected, (state, action) => {
      state.editInternalError = true;
    });

    builder.addCase(beneInternalDashboard.pending, (state) => {
      state.beneInternalDashboardAccLoading = true;
      state.internalAccountAvailableLabels = [];
    });
    builder.addCase(beneInternalDashboard.fulfilled, (state, action) => {
      state.internalAccountAvailableLabels =
        action?.payload?.summary?.available_labels;
      if (action?.payload?.summary?.available_labels) {
        sessionStorage.setItem(
          "AvailableLabelsInternal",
          JSON.stringify(action?.payload?.summary?.available_labels || []),
        );
      }
      state.beneInternalDashboardAccLoading = false;
      state.beneInternalDashboardAccData = action.payload;
    });
    builder.addCase(beneInternalDashboard.rejected, (state) => {
      state.beneInternalDashboardAccError = true;
      state.beneInternalDashboardAccLoading = false;
      state.beneInternalDashboardAccData = null;
      state.internalAccountAvailableLabels = [];
    });
    builder.addCase(beneInternalViewAllAcc.pending, (state) => {
      state.beneInternalViewAllAccLoading = true;
      state.internalAccountAvailableLabels = [];
    });
    builder.addCase(beneInternalViewAllAcc.fulfilled, (state, action) => {
      state.beneInternalViewAllAccLoading = false;
      state.beneInternalViewAllAccData = action.payload;
      state.internalAccountAvailableLabels =
        action?.payload?.summary?.available_labels;
      if (action?.payload?.summary?.available_labels) {
        sessionStorage.setItem(
          "AvailableLabelsInternal",
          JSON.stringify(action?.payload?.summary?.available_labels || []),
        );
      }
    });
    builder.addCase(beneInternalViewAllAcc.rejected, (state) => {
      state.beneInternalViewAllAccError = true;
      state.beneInternalViewAllAccData = null;
      state.beneInternalViewAllAccLoading = false;
      state.internalAccountAvailableLabels = [];
    });
    builder.addCase(viewInternalAccountDetail.pending, (state) => {
      state.viewInternalAccountDetailLoading = true;
    });
    builder.addCase(viewInternalAccountDetail.fulfilled, (state, action) => {
      state.viewInternalAccountDetailLoading = false;
      state.viewInternalAccountDetailData = action.payload;
    });
    builder.addCase(viewInternalAccountDetail.rejected, (state) => {
      state.viewInternalAccountDetailLoading = false;
      state.viewInternalAccountDetailError = true;
      state.viewInternalAccountDetailData = null;
    });

    builder.addCase(beneInternalRequiredApproval.pending, (state) => {
      state.beneInternalRequiredApprovalLoading = true;
      state.internalAccountAvailableLabels = [];
    });
    builder.addCase(beneInternalRequiredApproval.fulfilled, (state, action) => {
      state.beneInternalRequiredApprovalLoading = false;
      state.beneInternalRequiredApprovalData = action.payload;
      state.internalAccountAvailableLabels =
        action?.payload?.summary?.available_labels;
      if (action?.payload?.summary?.available_labels) {
        sessionStorage.setItem(
          "AvailableLabelsInternal",
          JSON.stringify(action?.payload?.summary?.available_labels || []),
        );
      }
    });
    builder.addCase(beneInternalRequiredApproval.rejected, (state) => {
      state.beneInternalRequiredApprovalLoading = false;
      state.beneInternalRequiredApprovalError = true;
      state.beneInternalRequiredApprovalData = null;
      state.internalAccountAvailableLabels = [];
    });

    builder.addCase(beneInternalWaitingApproved.pending, (state, action) => {
      state.beneInternalWaitingApprovalLoading = true;
    });
    builder.addCase(beneInternalWaitingApproved.fulfilled, (state, action) => {
      state.beneInternalWaitingApprovalLoading = false;
      state.beneInternalWaitingApprovalData = action.payload;
    });
    builder.addCase(beneInternalWaitingApproved.rejected, (state, action) => {
      state.beneInternalWaitingApprovalError = true;
      state.beneInternalWaitingApprovalData = null;
      state.beneInternalWaitingApprovalLoading = false;
    });
    builder.addCase(beneInternalDeniedApproval.pending, (state, action) => {
      state.beneInternalDeniedApprovalLoading = true;
    });

    builder.addCase(beneInternalDeniedApproval.fulfilled, (state, action) => {
      state.beneInternalDeniedApprovalLoading = false;
      state.beneInternalDeniedApprovalData = action.payload;
    });
    builder.addCase(beneInternalDeniedApproval.rejected, (state, action) => {
      state.beneInternalDeniedApprovalError = true;
      state.beneInternalDeniedApprovalData = null;
      state.beneInternalDeniedApprovalLoading = false;
    });
  },
});
export const {
  resetViewInternalAccountDetailData,
  resetInternalToggle,
  resetEditInternalData,
} = internalSlice.actions;
export default internalSlice.reducer;
